import { useMemo, useState } from 'react'
import {
  Button,
  Card,
  DateRangePicker,
  FilterBar,
  Kpi,
  PageHead,
  Select,
  Skeleton,
  StateBlock,
  resolveRangePreset,
  type DateRangePresetId,
} from '@w7/ui'
import { useChildren, useSession } from '@/app/queries'
import { Amount, AsOf } from '@/app/bits'
import { useFancyBookmakerCommission } from '@/api/hooks/reports/useFancyBookmakerCommission'
import { EMPTY, ll } from '@/lib/format'
import { ROLE_LABEL } from '@/data/types'

const PRESETS: DateRangePresetId[] = ['today', 'yesterday', 'last-7', 'last-30', 'this-week', 'custom']

function presetOf(from: string, to: string): DateRangePresetId {
  for (const id of PRESETS) {
    const resolved = resolveRangePreset(id)
    if (resolved && resolved.from === from && resolved.to === to) return id
  }
  return 'custom'
}

function rangeSummary(from: string, to: string): string {
  if (!from || !to) return ''
  return from === to ? ll(from) : `${ll(from)} – ${ll(to)}`
}

/**
 * Fancy & Bookmaker — Lab Account Statement → Commission.
 *
 * Product-split upline / downline / profit tiles. Demo fixtures offline;
 * live posts Lab `getTotalCommissionOnDetailPage` (memberId = self).
 */
export function FancyBookmakerCommissionPage() {
  const { data: session } = useSession()
  const selfId = session?.account.id
  const children = useChildren(selfId)

  const defaultRange = useMemo(() => resolveRangePreset('last-7')!, [])
  const [childId, setChildId] = useState('')
  const [from, setFrom] = useState(defaultRange.from)
  const [to, setTo] = useState(defaultRange.to)
  const [searched, setSearched] = useState(false)

  const query = useMemo(
    () => ({
      memberId: selfId ?? '',
      childMemberId: childId || undefined,
      from,
      to,
    }),
    [selfId, childId, from, to],
  )
  const report = useFancyBookmakerCommission(query, searched && Boolean(selfId))

  const options = children.data ?? []
  const child = options.find((a) => a.id === childId)

  return (
    <>
      <PageHead
        title="Fancy & Bookmaker commission"
        subtitle={`${
          session ? `${session.account.username} · ${ROLE_LABEL[session.account.role]}` : EMPTY
        } — upline and downline commission by product`}
        actions={report.data ? <AsOf at={report.data.asOf} /> : null}
      />

      <FilterBar label="Fancy & Bookmaker filters">
        <Select
          aria-label="Downline"
          value={childId}
          onChange={(e) => {
            setChildId(e.target.value)
            setSearched(false)
          }}
          placeholder="Select downline…"
          options={options.map((a) => ({
            value: a.id,
            label: `${a.username}${a.role ? ` · ${ROLE_LABEL[a.role]}` : ''}`,
          }))}
        />
        <DateRangePicker
          label="Date range"
          preset={presetOf(from, to)}
          value={{ from, to }}
          summary={rangeSummary(from, to)}
          presets={PRESETS}
          onChange={(next) => {
            setFrom(next.from)
            setTo(next.to)
            setSearched(false)
          }}
        />
        <Button
          variant="secondary"
          disabled={!childId || !from || !to || report.isFetching}
          busy={report.isFetching}
          onClick={() => setSearched(true)}
        >
          Search
        </Button>
      </FilterBar>

      {!searched ? (
        <StateBlock icon="commission" title="Choose a downline and range">
          Pick who to read and the dates, then Search — same flow as the Lab Fancy & Bookmaker
          screen.
        </StateBlock>
      ) : report.isLoading ? (
        <Skeleton height={180} />
      ) : report.data?.unavailable ? (
        <StateBlock icon="info" title="Fancy & Bookmaker commission is not available yet">
          {report.data.reason ??
            'Agent-API does not expose the Lab detail endpoint yet. Demo mode shows sample figures.'}
        </StateBlock>
      ) : report.data ? (
        <>
          {child ? (
            <p className="w7-dim" style={{ margin: 0 }}>
              Showing {child.username}
              {child.role ? ` · ${ROLE_LABEL[child.role]}` : ''}
            </p>
          ) : null}
          <Card title="Fancy" subtitle="Upline, downline, and net profit commission">
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}
            >
              <Kpi label="Upline" value={<Amount value={report.data.fancy.upline} />} />
              <Kpi label="Downline" value={<Amount value={-Math.abs(report.data.fancy.downline)} />} />
              <Kpi label="Profit" value={<Amount value={report.data.fancy.profit} />} />
            </div>
          </Card>
          <Card title="Bookmaker" subtitle="Upline, downline, and net profit commission">
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}
            >
              <Kpi label="Upline" value={<Amount value={report.data.bookmaker.upline} />} />
              <Kpi
                label="Downline"
                value={<Amount value={-Math.abs(report.data.bookmaker.downline)} />}
              />
              <Kpi label="Profit" value={<Amount value={report.data.bookmaker.profit} />} />
            </div>
          </Card>
        </>
      ) : null}
    </>
  )
}
